import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Router from 'next/router';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import getUuidByString from 'uuid-by-string';
import { Button, Grid, CircularProgress } from '@material-ui/core';
import ApiService from '../../api/restApi';
import { useQalert } from '../../utility/services/customHooks';
import { useCustomerInfo } from '../../utility/services/customHooks/auth';
import { useDispatch, useSelector } from 'react-redux';
import noImage from '../../../src/assets/images/noImage.png';
import { actionTypes } from '../../constants/actionTypes';
import { FormAutoComplete } from '../formElements';
import { DialogActions, Card, CardContent, Typography, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AddPageToOptimize from '../content-recommendations/AddPageToOptimize';
import ThreeBarLoader from '../../components/shared/ThreeBarLoader';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import { appendHttpsIfNotPresent, _RESOLUTION_ENTITY_TYPE } from '../../utility/common';
import { isEmpty } from 'lodash';
import {
  getCRDetailPagePath,
  LOCAL_NEW_PAGE_DATA,
  getPageDraftDetailPagePath,
} from '../../utility/services/crListHelper';
import { format } from 'date-fns';
import { getHostNameList } from '../../utility/validation/urls';
import { getCountryDetailsByAlpfa2, getCountryDetailsByAlpfa3 } from '../../constants/CountryAndLanguages';
import { COMPONENT_NAMES, SCREEN_NAMES, trackAnalyticsEvent } from '../../constants/analyticsMeta';
import { DEFAULT_TOP_GSC_DFS_ROW_LIMIT, CR_STEPS_PROGRESS } from '../../../src/constants/content-recommendations';
import { dispatchMasterDataQuery } from '../../utility/services/api/common';
import { changeStatusForPageHandler } from '../../utility/cr-common';
import { ANNOTATION_SUB_TYPE_VALUE, ANNOTATION_TYPE } from '../../constants/formMeta/recommendation';
import { useUser } from '@auth0/nextjs-auth0/client';
// import ListHoverActions from '../../components/shared/ListHoverActions';

type propsType = {
  setShowCreateNewPagePopUp: (params: boolean) => void;
};

interface typeCountryData {
  alpha_2?: string;
  alpha_3?: string;
  text?: string;
  value?: string;
  location_code?: string;
  forTempSolu?: number;
}

interface typeDomainData {
  text?: string;
  value?: string;
  disabled?: boolean;
}

const DEFAULT_COUNTRY: typeCountryData = {
  text: 'United States of America',
  value: 'United States of America',
  alpha_3: 'USA',
  alpha_2: 'US',
};
interface typeLanguageData {
  country_iso_code?: string;
  language_code?: string;
  // language_name?: string;
  location_code?: string | number | null;
  text?: string;
  value?: string;
  forTempSolu?: number;
}

const DEFAULT_LANGUAGE: typeLanguageData = {
  text: 'English',
  value: 'English',
  language_code: 'en',
  location_code: '2840',
  country_iso_code: 'US',
};

const AddGSCPageToOptimizeModal: React.FunctionComponent<propsType> = ({ setShowCreateNewPagePopUp }) => {
  const [selectedCountry] = useState<typeCountryData>({ ...DEFAULT_COUNTRY });
  const [selectedDomainList, setSelectedDomainList] = useState<any>();
  const [selectedDomain, setSelectedDomain] = useState<typeDomainData>();
  const [selectedPageUrls, setSelectedPageUrls] = useState<any>([]);
  const [selectedPageIndex, setSelectedPageIndex] = useState(null);
  const [topPagesCount, setTopPagesCount] = useState<number>(0);
  const [isAllPagesInclude] = useState<boolean>(false);
  const [additionalMessage, setAdditionalMessage] = useState('Fetching domains. Please wait a moment.');
  const [selectedLanguage] = useState({ ...DEFAULT_LANGUAGE });
  const [domainsList, setDomainsList] = useState<string[]>([]);
  const [uniqueUrls, setUniqueUrls] = useState<string[]>([]);
  const [customerDomain, setCustomerDomain] = useState<string>('');
  const [showCreateNewPagePopUpByUrl, setShowCreateNewPagePopUpByUrl] = useState(false);
  const [addByUrl, setAddByUrl] = useState(false);
  const [isApiInProcess, setIsApiInProcess] = useState(false);
  const [isLoadingLoadMore, setIsLoadingLoadMore] = useState(false);
  const [isAllPagesExceeded, setIsAllPagesExceeded] = useState(false);
  const { user } = useUser();
  const containerRef = useRef<HTMLDivElement>(null);
  const [top10PagesResponse, setTop10PagesResponse] = useState<any>([]);
  const [startRow, setStartRow] = useState(0);
  const [limit] = useState(10);

  const [, toggleSnackBar] = useQalert();
  const dispatch = useDispatch();
  const masterLanguageAndCountry: { countryData: any[]; languageData: any[] } | object = useSelector(
    (state) => state.root.masterLanguageAndCountry
  );
  const customerInfo = useSelector((state) => state.root.customerInfo) || [];
  const custDetails = useCustomerInfo(customerInfo);
  useEffect(() => {
    if (custDetails?.domain_url) {
      setCustomerDomain(new URL(custDetails?.domain_url)?.hostname);
    }
  }, [custDetails]);

  const getLanguageAndCountryData = useCallback(async () => {
    try {
      const response: { data: any[] } = await ApiService.get(
        `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/master-data/country-language`
      );
      dispatch({
        type: actionTypes.MASTER_COUNTRY_AND_LANGUAGE,
        payload: response.data,
      });
    } catch (error) {
      toggleSnackBar({ type: 'error', text: 'Oops! Something Went Wrong. Please try again.', open: true });
      setShowCreateNewPagePopUp(false);
    }
  }, [dispatch, setShowCreateNewPagePopUp, toggleSnackBar]);

  const fetchTop10GSCDFSPages = useCallback(
    async (start = 0, limit = 10, isFromLoadMore = false, filteDomain, uniqueURLsResponse: string[] = []) => {
      try {
        if (!isFromLoadMore) {
          setIsApiInProcess(true);
        }
        setIsAllPagesExceeded(false);
        const locationCodeBasedOnCountry: any =
          getCountryDetailsByAlpfa3(custDetails?.primary_country) || selectedCountry;
        const payload = {
          customer_url: appendHttpsIfNotPresent(filteDomain) || custDetails?.domain_url,
          country_language_data: {
            country_code: locationCodeBasedOnCountry?.alpha_2,
            language_code: selectedLanguage?.language_code,
            location_code: locationCodeBasedOnCountry?.location_code || DEFAULT_LANGUAGE?.location_code,
          },
          content_ai: true,
          is_discoverPages: true,
          schemaName: custDetails?.cust_code,
          start_row: start,
          row_limit: limit || DEFAULT_TOP_GSC_DFS_ROW_LIMIT,
        };
        const listResponse = await ApiService.post(
          `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/recommendations/content-recommendations/top-gsc-dfs-pages`,
          payload
        );
        if (listResponse?.data?.success) {
          return listResponse?.data;
        }
        const gscPageList = listResponse?.data?.filter((item) => !item?.url?.includes('/#'));
        console.log(
          '/#Urls:',
          listResponse?.data?.filter((item) => item?.url?.includes('/#'))
        );
        const filteredPagesData = gscPageList?.filter((item) => {
          return uniqueURLsResponse?.length ? !uniqueURLsResponse?.includes(item?.url) : true;
        });
        setIsAllPagesExceeded(filteredPagesData?.length < 2 ? true : false);
        setTop10PagesResponse((prevData) => {
          const newData = start > 0 ? [...prevData, ...filteredPagesData] : filteredPagesData;
          // Remove duplicates based on the URL
          const uniqueData = newData?.reduce((unique, item) => {
            return unique.some((u) => u.url === item.url) ? unique : [...unique, item];
          }, []);
          //setIsAllPagesInclude(uniqueData?.length === 0 ? true : false);
          setTopPagesCount(uniqueData?.length);
          setAdditionalMessage(
            !uniqueData?.length
              ? `No pages found. Opt for the 'Add Page By URL' option to manually include your page.`
              : ''
          );
          return uniqueData;
        });
      } catch (error) {
        console.error('Add:: fetchTop10GSCDFSPages :: error :: ', error);
      } finally {
        setIsApiInProcess(false);
      }
    },
    [
      custDetails?.cust_code,
      custDetails?.domain_url,
      custDetails?.primary_country,
      selectedCountry,
      selectedLanguage?.language_code,
    ]
  );

  const fetchDomains = useCallback(
    async (filteDomain = null) => {
      try {
        setTop10PagesResponse([]);
        setSelectedPageIndex(null);
        setSelectedPageUrls([]);
        setIsApiInProcess(true);
        let allDomains: string[] = [];

        if (filteDomain) {
          setAdditionalMessage('Scanning your website to discover pages to optimize. This may take a minute.');
        }

        const [masterDomainResponse, uniqueURLsResponse, domainSetupResponse] = await Promise.all([
          !filteDomain ? dispatchMasterDataQuery(dispatch, ['domain', 'market_share_segment']) : [],
          ApiService.get(`${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/master-data/unique-urls/`),
          !filteDomain
            ? ApiService.get(`${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/settings/setup?type=domain`)
            : [],
        ]);

        if (!filteDomain) {
          allDomains = [
            ...(getHostNameList(domainSetupResponse?.data?.domainSetup?.SUPPORTED_DOMAINS) || ([] as string[])),
          ];

          setIsApiInProcess(allDomains?.length > 0 ? false : true);
          setSelectedDomain(allDomains?.length > 0 ? { value: '', text: '', disabled: true } : selectedDomain);
          setAdditionalMessage(
            allDomains?.length > 0 ? 'Select a domain from the above dropdown to discover pages' : ''
          );
          if (masterDomainResponse?.domain?.length) {
            masterDomainResponse?.domain?.forEach((domain) => {
              if (domain?.top_data) {
                allDomains.push(new URL(domain?.top_data)?.hostname); //// it will check url have same hostname or not example abc.com or www.abc.com
              }
            });
          }
          setDomainsList(allDomains);
        }
        setUniqueUrls(uniqueURLsResponse);
        if (allDomains?.length <= 0 || filteDomain) {
          fetchTop10GSCDFSPages(
            0,
            10,
            false,
            filteDomain,
            uniqueURLsResponse?.unique_urls?.map((uu: { url: any }) => uu.url)
          );
        }
      } catch (error) {
        setIsApiInProcess(false);
        console.error('AddGSCPageToOptimize:: fetchDomains::error::', error);
      }
    },
    [dispatch, fetchTop10GSCDFSPages]
  );

  const fetchRequiredData = useCallback(async () => {
    try {
      const promiseArray = [fetchDomains()];
      if (isEmpty(masterLanguageAndCountry)) {
        promiseArray.push(getLanguageAndCountryData());
      }
      await Promise.all(promiseArray);
    } catch (error) {
      console.error('AddGSCPageToOptimize::fetchRequiredData::error::', error);
    }
  }, [fetchDomains, getLanguageAndCountryData, masterLanguageAndCountry]);

  const getCustDetailsHostname = useCallback((url) => {
    const hostname = new URL(url).hostname;
    return hostname;
  }, []);

  const DEFAULT_CUSTOMER_DOMAIN: typeCountryData = useMemo(
    () => ({
      text: getCustDetailsHostname(custDetails?.domain_url)?.replace('www.', ''),
      value: getCustDetailsHostname(custDetails?.domain_url)?.replace('www.', ''),
    }),
    [custDetails?.domain_url, getCustDetailsHostname]
  );

  const cancelHandler = useCallback(() => {
    setShowCreateNewPagePopUp(false);
  }, [setShowCreateNewPagePopUp]);

  const formatDomainForDropDown = useCallback(async (list) => {
    const newList = list.map((ele) => {
      return { text: ele, value: ele };
    });
    const modifiedList = [...newList];
    setSelectedDomainList(modifiedList);
  }, []);

  useEffect(() => {
    fetchRequiredData();
  }, [fetchRequiredData]);

  useEffect(() => {
    formatDomainForDropDown(domainsList);
  }, [domainsList, formatDomainForDropDown]);

  const redirectHandler = useCallback((record) => {
    const { path, params } =
      record?.is_cnp === 'TRUE' ? getPageDraftDetailPagePath(record) : getCRDetailPagePath('', record);
    Router.push(path, params);
  }, []);

  const onSubmitHandler = async (country, language) => {
    if (custDetails) {
      setIsApiInProcess(true);
      try {
        const locationCodeBasedOnCountry = getCountryDetailsByAlpfa2(country.alpha_2) || selectedCountry;
        const additional_page_info = {
          country: locationCodeBasedOnCountry,
          language: { ...language, location_code: locationCodeBasedOnCountry?.location_code || null },
        };
        for (const page of selectedPageUrls || []) {
          trackAnalyticsEvent({
            eventFor: 'button',
            buttonText: 'form submit',
            counterName: 'Discover Pages Added to Optimize',
            isAddCount: true,
            user,
            payload: {
              label: 'Add New Pages To Optimize',
              customer_url: page || '',
              component_name: COMPONENT_NAMES.content_recommendations,
              screen_name: SCREEN_NAMES.content_recommendations,
            },
          });

          const crId = getUuidByString(`${custDetails.cust_code}_${page}`);
          const payload = {
            content_recommendation_id: crId,
            page: page,
            data_source: Router?.router?.query?.type === 'organic' ? 'ORG' : 'PAID',
            additional_page_info,
          };

          const [responses] = await Promise.all([
            ApiService.post(
              `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/recommendations/content-recommendations/create-page`,
              payload
            ),
            // changeStatusForPageHandler({
            //   pageId: payload?.content_recommendation_id,
            //   annotationSubtype: ANNOTATION_SUB_TYPE_VALUE.RESEARCH_KEYWORDS,
            //   isFromContentBriefScreen: false,
            // }),
          ]);

          const dateValue = new Date();
          const annotationPayload = {
            recommendation_id: crId,
            reason: ANNOTATION_SUB_TYPE_VALUE.RESEARCH_KEYWORDS,
            date: format(new Date(dateValue), 'MM/dd/yyyy'),
            entity_type: _RESOLUTION_ENTITY_TYPE.CONTENT_RECOMMENDATIONS,
            entity_id: crId,
            annotation_type: ANNOTATION_TYPE.RESOLUTION,
            annotation_subtype: ANNOTATION_SUB_TYPE_VALUE.RESEARCH_KEYWORDS,
            annotation_details: {},
          };
          try {
            await ApiService.post(
              `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/utility/annotation`,
              annotationPayload
            );
          } catch (error) {
            console.error('AddGSCPageToOptimizeModal :: changeStatusForPageHandler:: error', error);
          }

          if (responses && responses?.data) {
            const newPageRecord = { ...LOCAL_NEW_PAGE_DATA };
            newPageRecord.uuid = payload?.content_recommendation_id;
            newPageRecord.data_source = payload?.data_source;
            newPageRecord.finalurl = payload?.page;
            newPageRecord.site = custDetails.domain_url as string;
            newPageRecord.created_date = format(new Date(), 'yyyy-mm-dd');
            redirectHandler(newPageRecord);
          }
        }
      } catch (e) {
        setIsApiInProcess(false);
        console.error('AddGSCPageToOptimizeModal:: onSubmitHandler:: error::', e);
      }
    }
  };

  const handleCheckboxChange = (index) => {
    setSelectedPageIndex(index);
    // Update selectedPageUrls based on updatedSelectedPageIndex
    const updatedSelectedPageUrl = top10PagesResponse[index]?.url;
    setSelectedPageUrls(updatedSelectedPageUrl ? [updatedSelectedPageUrl] : []);
  };

  const handleCardboxChange = (index) => {
    handleCheckboxChange(index);
  };

  const handleLoadMore = async () => {
    try {
      setIsLoadingLoadMore(true);
      const isFromLoadMore = true;
      await fetchTop10GSCDFSPages(
        startRow + limit,
        limit,
        isFromLoadMore,
        selectedDomain?.value || null,
        (uniqueUrls as any)?.unique_urls?.map((uu: { url: any }) => uu.url)
      );
      setStartRow((prevStartRow) => prevStartRow + limit);
      setIsLoadingLoadMore(false);
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    } catch (error) {
      console.error('Error fetching additional data:', error);
    }
  };

  return (
    <div id="content_recommendation__div__add_new_page__model_0" className="cr-editor-model-wrapper">
      <Dialog
        PaperProps={{
          style: {
            width: 1120,
            maxWidth: 1120,
            borderRadius: '4px',
            height: '100%',
            marginTop: '-140px',
          },
        }}
        onClose={() => {}}
        maxWidth="md"
        open={true}
        data-testid="addNewKeywordDlgBoxWrapper"
        fullWidth
      >
        <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 9999 }}>
          <IconButton
            style={{
              color: 'rgba(0, 0, 0, 0.54)',
            }}
            onClick={cancelHandler}
          >
            <CloseIcon />
          </IconButton>
        </div>

        {top10PagesResponse?.length && !isApiInProcess ? (
          <DialogTitle style={{ padding: '10px 0px 0px 25px' }}>
            Here are {topPagesCount} top pages for{' '}
            {selectedDomain?.text?.replace('www.', '') || customerDomain?.replace('www.', '')}. Select any page to
            analyze with Quattr
          </DialogTitle>
        ) : null}
        <div style={{ margin: '8px 0' }}></div>
        <DialogContent>
          {!isApiInProcess ? (
            <Grid container spacing={2}>
              <Grid item xs={2} sm={2} md={2} lg={2}></Grid>
              <Grid item xs={8} sm={8} md={8} lg={8}>
                <FormAutoComplete
                  value={selectedDomain || DEFAULT_CUSTOMER_DOMAIN}
                  options={selectedDomainList}
                  label={'Select Domain'}
                  className={'mb-25 w-100'}
                  onChange={(value) => {
                    setSelectedDomain(value);
                    fetchDomains(value?.text);
                  }}
                  fullWidth
                  disableClearable
                />
              </Grid>
              <Grid item xs={2} sm={2} md={2} lg={2}></Grid>
            </Grid>
          ) : null}
          {isApiInProcess ? (
            <div style={{ textAlign: 'center', marginTop: '210px' }}>
              <div style={{ fontSize: '20px', fontWeight: '700' }}>{additionalMessage}</div>
              <ThreeBarLoader />
            </div>
          ) : null}
          {top10PagesResponse?.length && !isApiInProcess ? (
            <>
              <Grid container spacing={2}>
                {top10PagesResponse?.map((data, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <div style={{ display: 'flex', alignItems: 'center', padding: '10px', cursor: 'pointer' }}>
                      <Card
                        style={{
                          backgroundColor: selectedPageIndex === index ? '#E3E7F1' : 'white',
                          boxShadow: selectedPageIndex === index ? '0 0 4px 2px #E3E7F1' : 'none',
                          position: 'relative',
                          flex: 1, // Take remaining space
                        }}
                        onClick={() => handleCardboxChange(index)}
                      >
                        <CardContent style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <img
                            src={data?.baseImage ? data?.baseImage : noImage.src}
                            alt="Card"
                            style={{ width: '100px', height: '80px', marginRight: '10px' }}
                          />
                          <div>
                            <Typography style={{ fontSize: '16px' }} variant="h6">
                              {data?.title ? data?.title : 'Title Not Available'}
                            </Typography>
                            <Typography variant="body2"> URL: {data?.url}</Typography>
                          </div>
                          <div>
                            <IconButton
                              style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                borderRadius: '50%',
                                padding: 0,
                                color: selectedPageIndex === index ? '#3f51b5' : 'rgba(0, 0, 0, 0.54)',
                              }}
                              onClick={() => handleCheckboxChange(index)}
                            >
                              {selectedPageIndex === index ? (
                                <CheckCircleIcon fontSize="small" style={{ color: '#234497' }} />
                              ) : (
                                <RadioButtonUncheckedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </Grid>
                ))}
              </Grid>
              <Grid container justify="center" ref={containerRef}>
                {!isAllPagesExceeded ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleLoadMore()}
                    style={{ marginTop: '30px', textTransform: 'capitalize', cursor: 'pointer' }}
                    disabled={isLoadingLoadMore}
                  >
                    {isLoadingLoadMore ? <CircularProgress className="position-abs" size={20} /> : null}
                    Load More Pages
                  </Button>
                ) : null}
              </Grid>
            </>
          ) : !isApiInProcess ? (
            <DialogContent style={{ textAlign: 'center', marginTop: '100px' }}>
              <h2 style={{ fontWeight: '500' }}>{additionalMessage}</h2>
            </DialogContent>
          ) : null}
        </DialogContent>
        {!isApiInProcess ? (
          <>
            <DialogActions>
              <Grid container style={{ alignItems: 'center', justifyContent: 'start' }} className="mt-15 mb-12">
                <Grid item xs={12} sm={12} className="align-self-center">
                  <div style={{ float: 'right' }}>
                    <span style={{ marginRight: '8px' }}>
                      <Button
                        className={`create-keyword-cancel-btn`}
                        variant="outlined"
                        color="primary"
                        onClick={cancelHandler}
                        style={{ marginTop: '8px' }}
                        data-testid="cancelBtn"
                      >
                        Cancel
                      </Button>
                      <Button
                        className={`create-keyword-cancel-btn`}
                        variant={top10PagesResponse ? 'outlined' : 'contained'}
                        color="primary"
                        onClick={() => {
                          setShowCreateNewPagePopUpByUrl(true);
                          setAddByUrl(true);
                        }}
                        style={{ marginTop: '8px', textTransform: 'capitalize' }}
                        data-testid="cancelBtn"
                      >
                        Add Page By URL
                      </Button>
                      {top10PagesResponse ? (
                        <Button
                          className={`create-intent-cta`}
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            setAddByUrl(false);
                            setShowCreateNewPagePopUpByUrl(true);
                            trackAnalyticsEvent({
                              eventFor: 'button',
                              buttonText: 'form open',
                              payload: {
                                label: 'Add Page To Optimize',
                                component_name: COMPONENT_NAMES.content_recommendations,
                                screen_name: SCREEN_NAMES.content_recommendations,
                              },
                            });
                          }}
                          style={{ marginTop: '8px', textTransform: 'capitalize', boxShadow: 'none' }}
                          data-testid="addpageBtn"
                          disabled={isApiInProcess || !selectedPageUrls?.length}
                        >
                          {isApiInProcess ? <CircularProgress className="position-abs" size={20} /> : null}
                          Add Page to Optimize
                        </Button>
                      ) : null}
                      {showCreateNewPagePopUpByUrl ? (
                        <AddPageToOptimize
                          setShowCreateNewPagePopUp={setShowCreateNewPagePopUpByUrl}
                          isFromDiscoverPagesModal={!addByUrl}
                          discoverPagesModalSubmithandler={onSubmitHandler}
                          domainsListFetched={domainsList}
                        />
                      ) : null}
                    </span>
                  </div>
                </Grid>
              </Grid>
            </DialogActions>
            <div style={{ borderTop: '1px solid #ccc' }}></div>
          </>
        ) : null}
      </Dialog>
    </div>
  );
};
export default AddGSCPageToOptimizeModal;
